import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import { Button } from '@workspace/ui/components/Button';
import { Spinner } from '@workspace/ui/components/Spinner';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import bgImage from '~/assets/img/mcosmetic/card_bg.png';
import Icon from '~/components/Icon/Icon';
import { IconButton } from '~/components/isolated/IconButton';
import { userStore } from '~/stores';

interface ModalClientCardProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  isFullHeight?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const ModalClientCard: React.FC<ModalClientCardProps> = observer(
  ({ isFullHeight = false, isOpen, onClose, ...props }) => {
    const { t } = useTranslation();

    const { card, cardMeta } = userStore.mindboxLoyaltyStore;

    const handleRetry = () => {
      userStore.mindboxLoyaltyStore.requestLoyaltyCard();
    };

    useEffect(() => {
      if (!isOpen) return;

      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };

      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEsc);
        document.body.style.overflow = '';
      };
    }, [isOpen, onClose]);

    useEffect(() => {
      if (!isOpen) return;

      userStore.mindboxLoyaltyStore.requestLoyaltyCard();
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
      <div
        {...props}
        className={cn('fixed inset-0 z-50 flex items-end justify-center')}
      >
        <div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
          role="presentation"
        />

        <div
          className={cn(
            'relative w-full max-w-lg rounded-t-6.5 bg-center bg-cover bg-image-cover bg-pink-200',
            isFullHeight
              ? 'h-[calc(100%-max(env(safe-area-inset-top),40px))]'
              : 'h-[calc(50vh+20%)]',
          )}
          style={{ backgroundImage: `url(${bgImage})` }}
        >
          <div className="flex items-center justify-end p-4">
            <IconButton
              size="S"
              variant="outline"
              icon="close"
              onClick={onClose}
              aria-label={t('close')}
            />
          </div>

          <div className="flex flex-col items-center gap-4 px-6 pt-2 pb-safe-offset-6">
            <div className="flex aspect-square w-full items-center justify-center rounded-5 border border-stroke-quaternary bg-graphic-regular-white p-4">
              {cardMeta.isLoading && !card?.number ? (
                <Spinner />
              ) : card?.number ? (
                <QRCode
                  value={card.number}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                />
              ) : (
                <div className="flex flex-col items-center gap-4 px-4 text-center">
                  <Icon type="warning" size={48} className="text-icon-error" />
                  <Typography.Text300Regular className="text-text-secondary">
                    {t('errors:qrCodeLoadFailed')}
                  </Typography.Text300Regular>
                  <Button variant="outlined" size="sm" onClick={handleRetry}>
                    {t('phrases:tryAgain')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  },
);

ModalClientCard.displayName = 'ModalClientCard';
